import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

/**
 * AWS EventBridge cron helpers for the schedule builder.
 *
 * EventBridge cron is 6 fields with the wrapper:
 *   cron(minutes hours day-of-month month day-of-week year)
 * with ``?`` for the unused day field and day-of-week names SUN..SAT.
 *
 * The builder supports daily / weekly / monthly at one or more times that share
 * a minute (an AWS-cron limitation: one minute + a list of hours). Expressions
 * outside this shape are edited as raw cron instead.
 */

export type ScheduleFrequency = "daily" | "weekly" | "monthly";

export interface ScheduleBuilderState {
  frequency: ScheduleFrequency;
  times: string[]; // "HH:MM"
  weekdays: string[]; // "MON".."SUN" (weekly)
  dayOfMonth: number | ""; // 1..31 (monthly); "" while the field is being edited
}

export const WEEKDAYS = [
  "SUN",
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
] as const;

const WEEKDAY_LABEL: Record<string, string> = {
  SUN: "Sun",
  MON: "Mon",
  TUE: "Tue",
  WED: "Wed",
  THU: "Thu",
  FRI: "Fri",
  SAT: "Sat",
};

const pad = (n: number) => String(n).padStart(2, "0");

function parseTime(value: string): { h: number; m: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

/** True when every time shares the same minute (required for a single cron). */
export function timesShareMinute(times: string[]): boolean {
  const parsed = times.map(parseTime).filter(Boolean) as { m: number }[];
  if (parsed.length === 0) return false;
  return parsed.every((p) => p.m === parsed[0].m);
}

export function buildAwsCron(state: ScheduleBuilderState): string {
  const parsed = state.times.map(parseTime).filter(Boolean) as {
    h: number;
    m: number;
  }[];
  const minute = parsed.length ? parsed[0].m : 0;
  const hours = Array.from(new Set(parsed.map((p) => p.h))).sort(
    (a, b) => a - b,
  );
  const hourField = hours.length ? hours.join(",") : "0";

  if (state.frequency === "weekly") {
    const dows = WEEKDAYS.filter((d) => state.weekdays.includes(d));
    return `cron(${minute} ${hourField} ? * ${dows.join(",") || "MON"} *)`;
  }
  if (state.frequency === "monthly") {
    const dom =
      typeof state.dayOfMonth === "number" &&
      state.dayOfMonth >= 1 &&
      state.dayOfMonth <= 31
        ? state.dayOfMonth
        : 1;
    return `cron(${minute} ${hourField} ${dom} * ? *)`;
  }
  return `cron(${minute} ${hourField} * * ? *)`;
}

function stripWrapper(expr: string): string {
  const e = expr.trim();
  if (e.toLowerCase().startsWith("cron(") && e.endsWith(")")) {
    return e.slice(5, -1).trim();
  }
  return e;
}

function dowToLabel(token: string): string {
  if (/^\d+$/.test(token)) {
    // EventBridge: 1=SUN .. 7=SAT
    return WEEKDAY_LABEL[WEEKDAYS[(Number(token) - 1) % 7]] ?? token;
  }
  return WEEKDAY_LABEL[token.toUpperCase()] ?? token;
}

function toAmPm(hour: number, minute: number): string {
  const period = hour < 12 ? "AM" : "PM";
  return `${hour % 12 || 12}:${pad(minute)} ${period}`;
}

function formatTimes(minute: string, hour: string): string {
  if (/^\d+$/.test(minute) && hour.split(",").every((h) => /^\d+$/.test(h))) {
    const m = Number(minute);
    return hour
      .split(",")
      .map((h) => toAmPm(Number(h), m))
      .join(", ");
  }
  return `${hour}:${minute}`;
}

/** Short human description; falls back to the raw expression. */
export function summarizeAwsCron(expr: string): string {
  const fields = stripWrapper(expr).split(/\s+/);
  if (fields.length !== 6) return expr;
  const [minute, hour, dom, month, dow] = fields;
  const times = formatTimes(minute, hour);
  const dowSet = dow !== "*" && dow !== "?";
  const domSet = dom !== "*" && dom !== "?";

  if (dowSet && !domSet) {
    const days = dow.split(",").map(dowToLabel).join(", ");
    return `Weekly on ${days} at ${times}`;
  }
  if (domSet && !dowSet) {
    return `Monthly on day ${dom} at ${times}`;
  }
  if (!domSet && !dowSet && month === "*") {
    return `Daily at ${times}`;
  }
  return expr;
}

/**
 * Parse a builder-shaped expression back into builder state, or ``null`` when
 * the expression is more complex (ranges/steps/etc) and should be edited raw.
 */
export function parseAwsCronToBuilder(
  expr: string,
): ScheduleBuilderState | null {
  const fields = stripWrapper(expr).split(/\s+/);
  if (fields.length !== 6) return null;
  const [minute, hour, dom, , dow] = fields;
  if (!/^\d+$/.test(minute)) return null;
  const hourParts = hour.split(",");
  if (!hourParts.every((h) => /^\d+$/.test(h))) return null;

  const m = Number(minute);
  const times = hourParts.map((h) => `${pad(Number(h))}:${pad(m)}`);
  const dowSet = dow !== "*" && dow !== "?";
  const domSet = dom !== "*" && dom !== "?";

  if (dowSet && !domSet) {
    const weekdays = dow.split(",").map((t) => {
      if (/^\d+$/.test(t)) return WEEKDAYS[(Number(t) - 1) % 7];
      return t.toUpperCase();
    });
    if (!weekdays.every((d) => (WEEKDAYS as readonly string[]).includes(d))) {
      return null;
    }
    return { frequency: "weekly", times, weekdays, dayOfMonth: 1 };
  }
  if (domSet && !dowSet) {
    if (!/^\d+$/.test(dom)) return null;
    return {
      frequency: "monthly",
      times,
      weekdays: [],
      dayOfMonth: Number(dom),
    };
  }
  if (!domSet && !dowSet) {
    return { frequency: "daily", times, weekdays: [], dayOfMonth: 1 };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Timezone shifting (display only)
// ---------------------------------------------------------------------------

function localZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

interface ShiftedTime {
  h: number;
  m: number;
  dayShift: number;
}

/**
 * Convert a wall-clock time from one zone to another using TODAY (in the
 * source zone) as the representative date. Near-DST future occurrences may
 * be off by an hour - acceptable for a display hint.
 */
function shiftClockTime(
  h: number,
  m: number,
  fromTz: string,
  toTz: string,
): ShiftedTime {
  const nowInFrom = utcToZonedTime(new Date(), fromTz);
  const base = new Date(
    nowInFrom.getFullYear(),
    nowInFrom.getMonth(),
    nowInFrom.getDate(),
    h,
    m,
  );
  const instant = zonedTimeToUtc(base, fromTz);
  const inTo = utcToZonedTime(instant, toTz);
  const dayMs = 24 * 60 * 60 * 1000;
  const fromDay = new Date(
    nowInFrom.getFullYear(),
    nowInFrom.getMonth(),
    nowInFrom.getDate(),
  ).getTime();
  const toDay = new Date(
    inTo.getFullYear(),
    inTo.getMonth(),
    inTo.getDate(),
  ).getTime();
  return {
    h: inTo.getHours(),
    m: inTo.getMinutes(),
    dayShift: Math.round((toDay - fromDay) / dayMs),
  };
}

function rotateDow(dow: string, shift: number): string | null {
  if (shift === 0) return dow;
  const rotated = dow.split(",").map((t) => {
    const idx = /^\d+$/.test(t)
      ? (Number(t) - 1) % 7 // EventBridge: 1=SUN .. 7=SAT
      : WEEKDAYS.indexOf(t.toUpperCase() as (typeof WEEKDAYS)[number]);
    if (idx < 0) return null;
    return WEEKDAYS[(idx + shift + 7 * 2) % 7];
  });
  if (rotated.some((t) => t == null)) return null;
  return (rotated as string[]).join(",");
}

/**
 * Re-express a builder-shaped EventBridge cron in another timezone for
 * DISPLAY. Crossing midnight rotates weekly day names; when only SOME of the
 * times cross midnight the schedule cannot fit one cron anymore, so it is
 * split into one expression per day-shift group (e.g. Chicago
 * ``Weekly on Mon,Fri at 10 AM, 8 PM`` in GMT+3 becomes ``Mon,Fri at 6 PM``
 * plus ``Tue,Sat at 4 AM``). Returns ``null`` when the expression cannot be
 * shifted faithfully (non-builder shapes, monthly crossing midnight) -
 * callers fall back to showing the original zone.
 */
export function shiftAwsCron(
  expr: string,
  fromTz: string,
  toTz?: string,
): string[] | null {
  const target = toTz ?? localZone();
  if (target === fromTz) return [expr];
  const fields = stripWrapper(expr).split(/\s+/);
  if (fields.length !== 6) return null;
  const [minute, hour, dom, month, dow, year] = fields;
  if (!/^\d+$/.test(minute)) return null;
  const hourParts = hour.split(",");
  if (!hourParts.every((p) => /^\d+$/.test(p))) return null;

  const m = Number(minute);
  let shifted: ShiftedTime[];
  try {
    shifted = hourParts.map((p) =>
      shiftClockTime(Number(p), m, fromTz, target),
    );
  } catch {
    return null; // invalid zone
  }
  // AWS cron carries ONE minute for all hours; zone offsets are uniform on a
  // given date, so all entries keep sharing a minute.
  const newMinute = shifted[0].m;
  if (!shifted.every((s) => s.m === newMinute)) return null;

  const dowSet = dow !== "*" && dow !== "?";
  const domSet = dom !== "*" && dom !== "?";

  // Group hours by how far they crossed midnight; each group becomes its own
  // expression (order: earliest shift first, matching chronological reading).
  const groups = new Map<number, number[]>();
  shifted.forEach((s) => {
    const list = groups.get(s.dayShift) ?? [];
    list.push(s.h);
    groups.set(s.dayShift, list);
  });
  const dayShifts = Array.from(groups.keys()).sort((a, b) => a - b);

  if (domSet && !(dayShifts.length === 1 && dayShifts[0] === 0)) {
    // A day-of-month cannot cross midnight faithfully (month boundaries).
    return null;
  }
  if (!dowSet && !domSet) {
    // Daily stays daily regardless of midnight crossings - one expression.
    const hourField = Array.from(new Set(shifted.map((s) => s.h)))
      .sort((a, b) => a - b)
      .join(",");
    return [`cron(${newMinute} ${hourField} ${dom} ${month} ${dow} ${year})`];
  }

  const out: string[] = [];
  for (const shift of dayShifts) {
    const newDow = dowSet ? rotateDow(dow, shift) : dow;
    if (newDow == null) return null;
    const hourField = Array.from(new Set(groups.get(shift)!))
      .sort((a, b) => a - b)
      .join(",");
    out.push(
      `cron(${newMinute} ${hourField} ${dom} ${month} ${newDow} ${year})`,
    );
  }
  return out;
}
